import { Capacitor } from '@capacitor/core';

const permissionMessage = () =>
  Capacitor.isNativePlatform()
    ? 'Location permission denied. Please enable location access in your app settings.'
    : 'Location permission denied. Please enable location access in your browser settings.';

const unavailableMessage = () =>
  Capacitor.isNativePlatform()
    ? 'Location is not available on this device. Enable GPS and try again.'
    : 'Geolocation is not supported by your browser';

const isDenied = (status) => status === 'denied' || status === 'prompt-with-rationale';

const hasGranted = (status) =>
  status === 'granted' || status === 'limited';

const permissionFromResult = (result) =>
  result?.location || result?.coarseLocation || result?.locationWhenInUse || '';

const mapGeoError = (error) => {
  const code = error?.code;
  const message = String(error?.message || '').toLowerCase();
  if (
    code === 1
    || message.includes('denied')
    || message.includes('not authorized')
    || message.includes('permission')
  ) {
    return new Error(permissionMessage());
  }
  if (code === 2 || message.includes('unavailable')) {
    return new Error('Location information unavailable. Please check your device settings.');
  }
  if (code === 3 || message.includes('timeout')) {
    return new Error('Location request timed out. Please try again or check your GPS settings.');
  }
  return new Error(error?.message || 'Unable to retrieve location');
};

const toCoords = (position) => ({
  latitude: position.coords.latitude,
  longitude: position.coords.longitude,
  accuracy: position.coords.accuracy,
});

async function ensureNativePermission(Geolocation) {
  const current = await Geolocation.checkPermissions();
  let status = permissionFromResult(current);
  if (!hasGranted(status)) {
    const requested = await Geolocation.requestPermissions();
    status = permissionFromResult(requested);
  }
  if (hasGranted(status)) return;
  if (isDenied(status) || status === 'prompt') {
    throw new Error(permissionMessage());
  }
  throw new Error(permissionMessage());
}

async function getPositionNative() {
  const { Geolocation } = await import('@capacitor/geolocation');
  await ensureNativePermission(Geolocation);
  try {
    return toCoords(await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    }));
  } catch (highAccuracyError) {
    try {
      return toCoords(await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 30000,
      }));
    } catch (fallbackError) {
      throw mapGeoError(fallbackError || highAccuracyError);
    }
  }
}

function getPositionWeb() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error(unavailableMessage()));
      return;
    }
    const onSuccess = (position) => resolve(toCoords(position));
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (error) => {
        if (error.code === error.TIMEOUT) {
          navigator.geolocation.getCurrentPosition(
            onSuccess,
            (fallbackError) => reject(mapGeoError(fallbackError)),
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 30000 },
          );
          return;
        }
        reject(mapGeoError(error));
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  });
}

export async function getCurrentDevicePosition() {
  if (Capacitor.isNativePlatform()) {
    try {
      return await getPositionNative();
    } catch (error) {
      const message = String(error?.message || '');
      if (message.includes('not implemented') || message.includes('plugin')) {
        return getPositionWeb();
      }
      throw error instanceof Error ? error : mapGeoError(error);
    }
  }
  return getPositionWeb();
}
